import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import DataTable from '../../components/DataTable.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import StatCard from '../../components/StatCard.jsx';
import Badge from '../../components/Badge.jsx';
import Modal from '../../components/Modal.jsx';
import { Field, Select, TextInput } from '../../components/Field.jsx';
import { formatCurrency, formatDate, fullName } from '../../utils/format.js';

/** Whole days late, or 0 - a balance settled on the due date is not late. */
const daysLate = (document) => {
  if (!document.dueDate || document.balance <= 0) return 0;
  const diff = Date.now() - new Date(document.dueDate).getTime();
  return diff > 0 ? Math.floor(diff / (24 * 3600 * 1000)) : 0;
};

/**
 * COLLECTIONS - who owes what, and since when.
 *
 * Built on invoices only. A quote is not owed, and a receipt is the proof of a
 * payment that was already counted against its invoice, so counting either here
 * would inflate the figure the whole screen exists to give.
 *
 * The three tiles are the only numbers that matter: billed, collected, and
 * still owed - with the late share of that last one called out, because an
 * outstanding balance that is not yet due is not a collections problem.
 *
 * Registering a payment is the one write. It goes through the API, which
 * refuses anything above the outstanding balance and moves the invoice to
 * PARTIAL or PAID on its own.
 */
const ReceivablesPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const [filter, setFilter] = useState({ q: '', state: 'due' });
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, loading, refresh } = useApi(
    () => documentApi.list({ type: 'INVOICE', q: filter.q || undefined, pageSize: 200 }),
    [filter.q],
  );

  const invoices = (data?.items ?? []).filter((row) => row.status !== 'CANCELLED');

  const rows = invoices.filter((row) => {
    if (filter.state === 'due') return row.balance > 0;
    if (filter.state === 'late') return row.balance > 0 && daysLate(row) > 0;
    if (filter.state === 'paid') return row.balance <= 0;
    return true;
  });

  // Over every invoice, not just the filtered rows: "collected" has to mean
  // collected in total, or the three tiles stop adding up.
  const sum = (list, pick) => list.reduce((total, row) => total + (pick(row) || 0), 0);
  const totals = {
    invoiced: sum(invoices, (row) => Number(row.total)),
    paid: sum(invoices, (row) => Number(row.paidAmount)),
    balance: sum(invoices, (row) => row.balance),
    late: sum(invoices.filter((row) => daysLate(row) > 0), (row) => row.balance),
  };

  const money = (value, currency) => formatCurrency(value, locale, currency);

  /** No email goes out - it stamps the account and raises a dated follow-up. */
  const chase = async (document) => {
    try {
      await documentApi.remind(document.id);
      toast.success(t('documents.reminded'));
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const registerPayment = async () => {
    setBusy(true);
    try {
      await documentApi.payment(payment.document.id, Number(payment.amount));
      toast.success(t('receivables.paymentSaved'));
      setPayment(null);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'number',
      header: t('documents.number'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.number}</span>
          <span className="cell-muted">{formatDate(row.issueDate, locale)}</span>
        </div>
      ),
    },
    { key: 'customerName', header: t('customers.companyName'), render: (row) => row.customerName ?? '-' },
    { key: 'owner', header: t('customers.commercial'), render: (row) => fullName(row.owner) },
    {
      key: 'dueDate',
      header: t('documents.dueDate'),
      render: (row) => {
        const late = daysLate(row);
        if (!row.dueDate) return '-';
        return late > 0 ? (
          <Badge value="LOST" label={t('receivables.daysLate', { count: late })} />
        ) : (
          formatDate(row.dueDate, locale)
        );
      },
    },
    {
      key: 'total',
      header: t('documents.total'),
      className: 'cell-right',
      render: (row) => money(row.total, row.currency),
    },
    {
      key: 'paidAmount',
      header: t('receivables.collected'),
      className: 'cell-right',
      render: (row) => money(row.paidAmount, row.currency),
    },
    {
      key: 'balance',
      header: t('receivables.balance'),
      className: 'cell-right',
      render: (row) =>
        row.balance > 0 ? (
          <span className="cell-strong">{money(row.balance, row.currency)}</span>
        ) : (
          <Badge value="WON" label={t('receivables.settled')} />
        ),
    },
    {
      key: 'action',
      header: '',
      className: 'cell-right',
      render: (row) =>
        row.balance > 0 ? (
          <div className="header-actions" onClick={(event) => event.stopPropagation()}>
            {daysLate(row) > 0 ? (
              <button type="button" className="btn btn--small" onClick={() => chase(row)}>
                {t('documents.remind')}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn--small btn--primary"
              onClick={() => setPayment({ document: row, amount: String(row.balance) })}
            >
              {t('receivables.registerPayment')}
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader title={t('receivables.title')} subtitle={t('receivables.subtitle')} />

      <div className="stat-grid">
        <StatCard label={t('receivables.invoiced')} value={money(totals.invoiced)} />
        <StatCard label={t('receivables.collected')} value={money(totals.paid)} tone="success" />
        <StatCard
          label={t('receivables.balance')}
          value={money(totals.balance)}
          tone={totals.balance > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label={t('receivables.lateAmount')}
          value={money(totals.late)}
          tone={totals.late > 0 ? 'danger' : 'default'}
          hint={t('receivables.lateHint')}
        />
      </div>

      <Toolbar>
        <div className="segmented" role="group">
          {['due', 'late', 'paid', 'all'].map((state) => (
            <button
              key={state}
              type="button"
              className={filter.state === state ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
              onClick={() => setFilter((current) => ({ ...current, state }))}
            >
              {t(`receivables.filter.${state}`)}
            </button>
          ))}
        </div>
        <TextInput
          value={filter.q}
          onChange={(value) => setFilter((current) => ({ ...current, q: value }))}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
        />
        <span className="toolbar__count">
          {rows.length} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyTitle={t('receivables.empty')}
        onRowClick={(row) => navigate(`/documents/${row.id}`)}
      />

      <Modal
        open={Boolean(payment)}
        title={t('receivables.registerPayment')}
        onClose={() => setPayment(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setPayment(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" onClick={registerPayment} disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </>
        }
      >
        {payment ? (
          <div className="form-grid">
            <div className="span-2">
              <p className="static-value">
                {payment.document.number} - {payment.document.customerName ?? '-'}
              </p>
              <p className="field__hint">
                {t('receivables.outstanding')} {money(payment.document.balance, payment.document.currency)}
              </p>
            </div>
            <div className="span-2">
              <Field label={t('receivables.amount')} htmlFor="pay-amount" required>
                <TextInput
                  id="pay-amount"
                  type="number"
                  value={payment.amount}
                  onChange={(value) => setPayment((current) => ({ ...current, amount: value }))}
                />
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default ReceivablesPage;
